/**
 * AlmanacAdminNew — super-admin only page for generating almanac
 * records via Claude Sonnet 4.5.
 *
 * Route: /admin/almanac/new
 *
 * Three inputs: location, event type, optional date + context. On
 * Generate, streams a spinner while the backend runs the LLM +
 * Wikipedia enrichment (typically 30-90 s), then jumps to the
 * public record on success.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Sparkles, ArrowLeft, AlertTriangle } from "lucide-react";

const EVENT_TYPES = [
  "Heat Wave", "Cold Wave", "Blizzard", "Ice Storm", "Snowstorm",
  "Tropical Cyclone", "Hurricane", "Typhoon", "Tornado", "Tornado Outbreak",
  "Severe Thunderstorm", "Flash Flood", "River Flood", "Coastal Flood",
  "Atmospheric River", "Drought", "Dust Storm", "Sandstorm", "Monsoon",
  "Hailstorm", "Lightning Event", "Aurora", "Fog Event", "Wildfire Smoke",
  "Volcanic Ash", "Record Temperature", "Record Rainfall", "Record Snowfall",
  "Historic Storm", "Climate Milestone", "Rare Weather Event",
];

export default function AlmanacAdminNew() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [location, setLocation] = useState("");
  const [eventType, setEventType] = useState("River Flood");
  const [date, setDate] = useState("");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const isAdmin = user && (user.is_admin || user.platform_role === "super_admin");

  // Fake progress ticker — Claude usually takes 30-90 s and the fetch
  // doesn't stream, so we simulate steady progress up to ~90% while we wait.
  const startTicker = () => {
    setProgress(0);
    const start = Date.now();
    const iv = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      // Approaches 90 asymptotically over ~60 s.
      const p = Math.min(90, 100 - 90 * Math.exp(-elapsed / 20));
      setProgress(p);
    }, 400);
    return () => clearInterval(iv);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!location.trim() || !eventType) return;
    setBusy(true);
    const stop = startTicker();
    try {
      const { data } = await http.post(
        "/weather-almanac/records/generate",
        {
          location: location.trim(),
          event_type: eventType,
          date: date || null,
          extra_context: extra || null,
          publish: true,
        },
        { timeout: 240_000 },
      );
      setProgress(100);
      toast.success("Almanac record created.");
      navigate(`/almanac/records/${data.slug || data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Generation failed.");
    } finally {
      stop();
      setBusy(false);
    }
  };

  if (authLoading) {
    return <div className="p-8"><Loader2 className="w-6 h-6 animate-spin opacity-60" /></div>;
  }
  if (!isAdmin) {
    return (
      <div className="max-w-2xl mx-auto p-8">
        <div className="wofy-card p-6 text-rose-700 border-rose-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Super-admin only.
        </div>
        <div className="mt-4">
          <Link to="/weather" className="wofy-btn-ghost text-sm"><ArrowLeft className="w-4 h-4" /> Back to weather</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6 sm:p-10" data-testid="almanac-admin-new">
      <Link to="/weather" className="text-sm inline-flex items-center gap-1 opacity-70 hover:opacity-100">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Weather
      </Link>
      <h1 className="mt-3 font-display text-3xl sm:text-4xl">Generate a Weather Almanac Record</h1>
      <p className="mt-2 opacity-70 text-sm">
        Claude Sonnet 4.5 builds a full encyclopedic record, then we backfill Wikipedia
        + Wikimedia Commons for authentic photos and links.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-widest opacity-70">Location</span>
          <input
            required
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Pala, Kottayam, Kerala, India"
            className="mt-1 w-full rounded-lg border px-3 py-2"
            data-testid="almanac-location"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-widest opacity-70">Event type</span>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2"
            data-testid="almanac-event-type"
          >
            {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-widest opacity-70">Date (optional)</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2"
            data-testid="almanac-date"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-widest opacity-70">Extra context (optional)</span>
          <textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Anything you want the LLM to know: named-storm, specific district, historic angle, etc."
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
            data-testid="almanac-extra"
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="wofy-btn-primary w-full disabled:opacity-60"
          data-testid="almanac-generate"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {busy ? "Generating (this takes 30-90 s)…" : "Generate record"}
        </button>
        {busy ? (
          <div className="w-full h-1 bg-neutral-200 rounded overflow-hidden">
            <div className="h-full bg-black transition-all" style={{ width: `${progress}%` }} data-testid="almanac-progress" />
          </div>
        ) : null}
      </form>

      <div className="mt-8 wofy-card !p-4 text-xs opacity-70">
        <strong>Note:</strong> the LLM leaves link arrays (news, videos, government, research)
        empty when it can't cite a real source — as instructed by the strict-provenance rule.
        Wikipedia and Wikimedia Commons are enriched automatically afterwards.
      </div>
    </div>
  );
}
